import { clampUnit, unitsUpTo } from './period-units';

/* Tenure rules for Fixed Deposit and Recurring/Target products, the same chain
   the loan product enforces: the tenure has a unit, and the periods that work
   inside it (compounding, and for Recurring the saving frequency) cannot be
   longer than it. TENOR means "once for the whole tenure", so it is always
   allowed for the interest rate and for compounding. */

export interface UnitOption {
  value: string;
  label: string;
}

export const TENURE_UNIT_OPTIONS: UnitOption[] = [
  { value: 'DAY', label: $localize`:@@dayOption:Day` },
  { value: 'WEEK', label: $localize`:@@weekOption:Week` },
  { value: 'MONTH', label: $localize`:@@monthOption:Month` },
  { value: 'YEAR', label: $localize`:@@yearOption:Year` },
];

export const TENOR_OPTION: UnitOption = {
  value: 'TENOR',
  label: $localize`:@@tenorOption:Tenor`,
};

/** Interest rate periods: every unit plus Tenor (the rate is for the whole tenure) */
export const INTEREST_PERIOD_OPTIONS: UnitOption[] = [...TENURE_UNIT_OPTIONS, TENOR_OPTION];

const UNIT_RANK = ['DAY', 'WEEK', 'MONTH', 'YEAR'];
const rank = (unit: string): number => UNIT_RANK.indexOf(String(unit ?? '').toUpperCase());

/**
 * Compounding choices for a tenure unit: no period longer than the tenure unit,
 * plus Tenor. `keep` (a value already saved) stays listed even when it breaks
 * the rule, so an existing product never shows a blank select.
 */
export function compoundingOptionsFor(tenureUnit: string, keep?: string): UnitOption[] {
  return [...unitsUpTo(TENURE_UNIT_OPTIONS, tenureUnit, keep), TENOR_OPTION];
}

/** The compounding value after the tenure unit changed: moved down when it is now longer than the tenure */
export function compoundingForTenure(compounding: string, tenureUnit: string): string {
  return clampUnit(compounding, tenureUnit);
}

/** The saving frequencies of a Recurring product as time units */
export function frequencyUnit(frequency: string): string {
  switch (String(frequency ?? '').toUpperCase()) {
    case 'DAILY':
      return 'DAY';
    case 'WEEKLY':
      return 'WEEK';
    case 'MONTHLY':
      return 'MONTH';
    case 'YEARLY':
      return 'YEAR';
    default:
      return '';
  }
}

/**
 * Tenure units a Recurring product can use: none shorter than its saving
 * frequency (weekly saving over a 3-day tenure never happens). All of them
 * when the frequency is not known.
 */
export function tenureUnitsForFrequency(frequency: string, keep?: string): UnitOption[] {
  const floor = rank(frequencyUnit(frequency));
  if (floor < 0) return TENURE_UNIT_OPTIONS;
  return TENURE_UNIT_OPTIONS.filter(
    (option) => rank(option.value) >= floor || option.value === keep,
  );
}

/** The tenure unit after the frequency changed: raised to the frequency's unit when it is now shorter */
export function tenureUnitForFrequency(tenureUnit: string, frequency: string): string {
  const floor = frequencyUnit(frequency);
  return rank(floor) >= 0 && rank(tenureUnit) < rank(floor) ? floor : tenureUnit;
}

/** "Day", "Week", ... for a tenure unit, for headings and calculator labels */
export function tenureUnitLabel(unit: string): string {
  return TENURE_UNIT_OPTIONS.find((option) => option.value === unit)?.label ?? '';
}
